import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  CalendarDays, CheckCircle2, ChefHat, ClipboardList, Loader2,
  FileImage, Package, Printer, RefreshCw, Scale, Tag, UtensilsCrossed,
} from 'lucide-react'
import { useApp } from '../App'
import { orderSourceLabel } from '../lib/cityWorkspace'
import { airtableAPI, menusAPI } from '../lib/api'
import { buildCustomerLabelGroups } from '../lib/customerLabels'

const SALAD_CATEGORIES = new Set(['salad', 'salad_2'])
const NON_CONTAINER_CATEGORIES = new Set([
  'broth', 'broth_2', 'oats', 'chia', 'chia_2', 'wellness',
  'wellness_shots', 'date_balls',
])

function totalContainers(mealCounts) {
  const totals = { mealGlass: 0, mealPlastic: 0, saladGlass: 0, saladPlastic: 0 }
  for (const [category, counts] of Object.entries(mealCounts || {})) {
    if (NON_CONTAINER_CATEGORIES.has(category)) continue
    const prefix = SALAD_CATEGORIES.has(category) ? 'salad' : 'meal'
    // `glass` and `plastic` already include standard + custom orders. Never add
    // glass_custom/plastic_custom again or customs would be double-counted.
    totals[`${prefix}Glass`] += Number(counts?.glass) || 0
    totals[`${prefix}Plastic`] += Number(counts?.plastic) || 0
  }
  return totals
}

function formatImportDate(value) {
  if (!value) return 'No import loaded'
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return value
  return parsed.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}

export default function OperationsHome() {
  const { currentCity, currentMenu, cities, menus } = useApp()
  const [menu, setMenu] = useState(null)
  const [latest, setLatest] = useState(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(null)
  const navigate = useNavigate()

  useEffect(() => {
    if (!currentCity || !currentMenu) {
      setMenu(null); setLatest(null); setLoading(false)
      return
    }
    let cancelled = false
    setLoading(true)
    setLoadError(null)
    Promise.all([
      menusAPI.get(currentMenu),
      airtableAPI.getLatest(currentCity, currentMenu).catch(() => null),
    ])
      .then(([menuData, importData]) => {
        if (cancelled) return
        setMenu(menuData)
        setLatest(importData)
      })
      .catch((err) => { if (!cancelled) setLoadError(err.message) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [currentCity, currentMenu])

  const containerTotals = useMemo(() => totalContainers(latest?.meal_counts), [latest])
  const labelCount = useMemo(
    () => buildCustomerLabelGroups(menu, latest?.customizations).totalLabels,
    [menu, latest]
  )
  const currentCityName = cities.find((city) => city.id === currentCity)?.name || currentCity?.toUpperCase()
  const currentMenuSummary = menus.find((entry) => entry.id === currentMenu)

  if (loading) {
    return <div className="flex justify-center py-24"><Loader2 className="w-7 h-7 text-havn-green-700 animate-spin" /></div>
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6 animate-fade-in">
      <section className="rounded-2xl bg-havn-green-900 text-white p-5 lg:p-7 shadow-soft">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-5">
          <div>
            <p className="text-xs uppercase tracking-[0.18em] text-havn-brass-400 font-medium">Weekly operations</p>
            <h1 className="font-display text-3xl mt-2">{menu?.name || currentMenuSummary?.name || 'Select a menu'}</h1>
            <p className="text-white/65 mt-1">{currentCityName}</p>
          </div>
          <button onClick={() => navigate('/kitchen')} className="min-h-[48px] px-5 rounded-xl bg-white text-havn-green-900 font-semibold inline-flex items-center justify-center gap-2">
            <UtensilsCrossed className="w-5 h-5" /> Open kitchen view
          </button>
        </div>
      </section>

      {loadError && (
        <div className="rounded-xl border border-red-200 bg-red-50 text-red-700 px-4 py-3">{loadError}</div>
      )}

      <section>
        <div className="flex items-center justify-between gap-3 mb-3">
          <div>
            <h2 className="font-display text-xl text-havn-green-900">Containers needed</h2>
            <p className="text-sm text-havn-gray-500">Regular and custom orders are included in each total.</p>
          </div>
          <span className="text-xs text-havn-gray-600">Latest import · {formatImportDate(latest?.imported_at)}</span>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            ['Meal glass', containerTotals.mealGlass],
            ['Meal plastic', containerTotals.mealPlastic],
            ['Salad glass', containerTotals.saladGlass],
            ['Salad plastic', containerTotals.saladPlastic],
          ].map(([label, value]) => (
            <div key={label} className="card p-4 lg:p-5">
              <p className="text-xs uppercase tracking-wider text-havn-gray-500">{label}</p>
              <p className="font-display text-3xl lg:text-4xl font-semibold text-havn-green-900 mt-2 tabular-nums">{value}</p>
              <p className="text-xs text-havn-gray-600 mt-1">needed</p>
            </div>
          ))}
        </div>
      </section>

      <div className="grid lg:grid-cols-[1.25fr_.75fr] gap-5">
        <section className="card">
          <div className="px-5 py-4 border-b border-havn-cream-200 flex items-center justify-between gap-3">
            <div>
              <h2 className="font-display text-lg text-havn-green-900">This week</h2>
              <p className="text-xs text-havn-gray-500">Everything needed to move the operation forward.</p>
            </div>
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-havn-green-50 text-havn-green-700 text-xs font-medium">
              <CheckCircle2 className="w-3.5 h-3.5" /> Latest import is authoritative
            </span>
          </div>
          <div className="divide-y divide-havn-cream-200">
            <ActionRow icon={ChefHat} title="Menu" detail={`${menu?.dishes?.length || 0} dishes · ${menu?.name || 'No menu selected'}`} action="Meal counts" onClick={() => navigate('/meal-counts')} />
            <ActionRow icon={RefreshCw} title={`${orderSourceLabel(currentCity)} counts`} detail={`${latest?.order_count || 0} accounts · ${latest?.customization_count || 0} custom orders · imported ${formatImportDate(latest?.imported_at)}`} action="Open counts" onClick={() => navigate('/meal-counts')} />
            <ActionRow icon={Package} title="Ordering" detail="Existing Ordering screen and calculations remain unchanged" action="Continue" onClick={() => navigate('/ordering')} />
            <ActionRow icon={Scale} title="Kitchen production" detail="Progress, weights, plating, and items needing attention" action="Open" onClick={() => navigate('/kitchen-overview')} />
          </div>
        </section>

        <section className="card">
          <div className="px-5 py-4 border-b border-havn-cream-200">
            <h2 className="font-display text-lg text-havn-green-900">Print center</h2>
            <p className="text-xs text-havn-gray-500">Administrative printing for the current menu.</p>
          </div>
          <div className="p-4 space-y-3">
            <button onClick={() => navigate('/print-materials')} className="w-full min-h-[58px] px-4 rounded-xl border border-havn-cream-300 hover:border-havn-green-600 bg-white text-left flex items-center gap-3">
              <FileImage className="w-5 h-5 text-havn-green-700" />
              <span><strong className="block text-sm text-havn-charcoal">Print materials</strong><span className="text-xs text-havn-gray-500">Menu card, order card, reheating card, salad card, and this week's stickers</span></span>
            </button>
            <button onClick={() => navigate('/recipes?scope=menu&view=scaled')} className="w-full min-h-[58px] px-4 rounded-xl border border-havn-cream-300 hover:border-havn-green-600 bg-white text-left flex items-center gap-3">
              <Printer className="w-5 h-5 text-havn-green-700" />
              <span><strong className="block text-sm text-havn-charcoal">All recipes</strong><span className="text-xs text-havn-gray-500">Scale to current menu, review, then bulk print everything</span></span>
            </button>
            <button onClick={() => navigate('/customs')} className="w-full min-h-[58px] px-4 rounded-xl border border-havn-cream-300 hover:border-havn-green-600 bg-white text-left flex items-center gap-3">
              <ClipboardList className="w-5 h-5 text-havn-green-700" />
              <span><strong className="block text-sm text-havn-charcoal">Customs by dish</strong><span className="text-xs text-havn-gray-500">Use the existing exact per-dish print sheets</span></span>
            </button>
            <button onClick={() => navigate('/customer-labels')} className="w-full min-h-[58px] px-4 rounded-xl border border-havn-cream-300 hover:border-havn-green-600 bg-white text-left flex items-center gap-3">
              <Tag className="w-5 h-5 text-havn-green-700" />
              <span><strong className="block text-sm text-havn-charcoal">Customer labels</strong><span className="text-xs text-havn-gray-500">{labelCount} PDF labels · grouped by dish · one per custom meal</span></span>
            </button>
          </div>
        </section>
      </div>

      <section className="card p-5">
        <div className="flex items-center gap-2 mb-4">
          <CalendarDays className="w-5 h-5 text-havn-green-700" />
          <h2 className="font-display text-lg text-havn-green-900">Import rhythm</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            ['Wednesday', 'Early ordering counts'],
            ['Thursday · 7 PM', 'Customer cutoff and main ordering update'],
            ['Friday', 'Late orders update remaining prep demand'],
            ['Saturday', 'Final counts and individual buffer changes'],
          ].map(([day, detail]) => (
            <div key={day} className="rounded-xl bg-havn-cream-100 border border-havn-cream-300 p-3">
              <strong className="text-sm text-havn-charcoal">{day}</strong>
              <p className="text-xs text-havn-gray-500 mt-1">{detail}</p>
            </div>
          ))}
        </div>
        <p className="text-xs text-havn-gray-500 mt-4">Re-importing recalculates customer counts, customs, container totals, and needed quantities. Existing manual Ordering entries remain saved.</p>
      </section>
    </div>
  )
}

function ActionRow({ icon: Icon, title, detail, action, onClick }) {
  return (
    <div className="px-4 lg:px-5 py-4 flex items-center gap-3">
      <span className="w-10 h-10 rounded-xl bg-havn-cream-100 text-havn-green-700 flex items-center justify-center flex-shrink-0"><Icon className="w-5 h-5" /></span>
      <div className="min-w-0 flex-1"><strong className="block text-sm text-havn-charcoal">{title}</strong><span className="block text-xs text-havn-gray-500 mt-0.5">{detail}</span></div>
      <button onClick={onClick} className="min-h-[40px] px-3 rounded-lg border border-havn-cream-300 text-sm font-medium text-havn-green-700 hover:bg-havn-cream-100 flex-shrink-0">{action}</button>
    </div>
  )
}
